import React, { useState } from 'react';

// ─── Design Tokens (matching coordinator app) ───────────────────────────────
const JADE = '#0D7C7C';
const JADE_LIGHT = '#0fa3a3';
const JADE_DARK = '#095e5e';
const CREAM = '#f5f0e8';
const INK = '#0f1a14';
const GREEN = '#16a34a';
const YELLOW = '#d97706';
const RED = '#dc2626';
const WHITE = '#ffffff';

// ─── Framework Data (100% compliant — demo mode) ────────────────────────────

interface ComplianceItem {
    req: string;
    status: 'compliant' | 'partial' | 'gap';
    detail: string;
}

interface ComplianceCategory {
    name: string;
    items: ComplianceItem[];
}

interface ComplianceFramework {
    id: string;
    name: string;
    fullName: string;
    jurisdiction: string;
    priority: string;
    total: number;
    compliant: number;
    partial: number;
    gap: number;
    categories: ComplianceCategory[];
}

const frameworks: ComplianceFramework[] = [
    {
        id: 'hipaa',
        name: 'HIPAA',
        fullName: 'Health Insurance Portability and Accountability Act',
        jurisdiction: 'United States',
        priority: 'Active',
        total: 25,
        compliant: 25,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Administrative Safeguards',
                items: [
                    { req: 'Security Management Process', status: 'compliant', detail: 'Risk analysis documented; QA criteria defined (112 items); rollback policy with 4 severity levels' },
                    { req: 'Assigned Security Responsibility', status: 'compliant', detail: 'Keycloak admin role with RBAC enforcement; platform admin designated' },
                    { req: 'Workforce Security', status: 'compliant', detail: 'Staff ENUM roles (doctor, coordinator, receptionist, nurse, admin); access provisioning via Keycloak' },
                    { req: 'Information Access Management', status: 'compliant', detail: 'requireDoctorScope middleware deployed; patient data isolation per QA criteria §1.3.2' },
                    { req: 'Security Awareness Training', status: 'compliant', detail: 'Annual security training program established; compliance documentation maintained' },
                    { req: 'Security Incident Procedures', status: 'compliant', detail: 'SEV1-SEV4 classification; response times defined; forensic procedures documented' },
                    { req: 'Contingency Plan', status: 'compliant', detail: '7-day backup retention; S3 7-year lifecycle; disaster recovery plan with RTO/RPO targets' },
                    { req: 'Evaluation', status: 'compliant', detail: '112 QA acceptance criteria validated; OWASP ZAP scan integrated in CI/CD pipeline' },
                    { req: 'Business Associate Agreements', status: 'compliant', detail: 'BAA executed with AWS, Twilio, Daily.co — all subprocessors covered' },
                ],
            },
            {
                name: 'Physical Safeguards',
                items: [
                    { req: 'Facility Access Controls', status: 'compliant', detail: 'AWS data centers (SOC 2 certified); patient data region-locked to us-east-1' },
                    { req: 'Workstation Use', status: 'compliant', detail: 'Keycloak session management; token-based auth; 15-min idle auto-logout enforced' },
                    { req: 'Workstation Security', status: 'compliant', detail: 'Browser-based access only; no PHI stored on local devices; PWA architecture' },
                    { req: 'Device & Media Controls', status: 'compliant', detail: 'Cloud-only architecture; S3 lifecycle policies; no removable media permitted' },
                ],
            },
            {
                name: 'Technical Safeguards',
                items: [
                    { req: 'Unique User Identification', status: 'compliant', detail: 'Keycloak SSO with unique accounts; UUID-based staff_id and patient_id' },
                    { req: 'Emergency Access Procedure', status: 'compliant', detail: 'Break-glass procedure documented; admin emergency access with audit logging' },
                    { req: 'Automatic Logoff', status: 'compliant', detail: '15-minute idle timeout; 8-hour absolute session limit; token refresh cycle enforced' },
                    { req: 'Encryption & Decryption', status: 'compliant', detail: 'AES-256 at rest; TLS 1.3 in transit; encryption enabled across all services' },
                    { req: 'Audit Controls', status: 'compliant', detail: 'Full audit_log table active with 51+ entries: actor, IP, resource, action, timestamp' },
                    { req: 'Integrity Controls', status: 'compliant', detail: 'Signed SOAP notes immutable; FHIR resource versioning; append-only clinical records' },
                    { req: 'Person Authentication', status: 'compliant', detail: 'Keycloak SSO (doctors); OTP login (patients); 3 Keycloak client applications' },
                    { req: 'Transmission Security', status: 'compliant', detail: 'TLS/HTTPS enforced; ACM SSL certificates active; WAF deployed' },
                ],
            },
            {
                name: 'Breach Notification',
                items: [
                    { req: 'Individual Notification', status: 'compliant', detail: 'WhatsApp + email notification pipeline for individual breach alerts' },
                    { req: 'HHS Notification', status: 'compliant', detail: 'Federal reporting procedure documented with 60-day compliance window' },
                    { req: 'Media Notification (500+)', status: 'compliant', detail: 'Mass notification procedure established for breaches affecting 500+ individuals' },
                    { req: 'Breach Risk Assessment', status: 'compliant', detail: 'Risk assessment framework with severity classification and response matrix' },
                ],
            },
        ],
    },
    {
        id: 'habeas',
        name: 'Habeas Data',
        fullName: 'Colombian Law 1581/2012 — Data Protection',
        jurisdiction: 'Colombia',
        priority: 'Active',
        total: 12,
        compliant: 12,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Data Subject Rights & Obligations',
                items: [
                    { req: 'Informed Consent (Art. 9)', status: 'compliant', detail: 'consent_forms table with digital signature capture; WhatsApp consent gate active' },
                    { req: 'Purpose Limitation (Art. 4)', status: 'compliant', detail: 'Data collected for clinical, billing, and communication purposes only' },
                    { req: 'Data Minimization (Art. 4)', status: 'compliant', detail: 'Schema collects only clinically necessary fields; no excessive data' },
                    { req: 'Data Quality / Accuracy (Art. 4)', status: 'compliant', detail: 'Patient Portal enables record viewing and correction requests; coordinator review' },
                    { req: 'Right to Access (Art. 14)', status: 'compliant', detail: 'Patient Portal displays encounters, prescriptions, lab results in real-time' },
                    { req: 'Right to Rectification (Art. 14)', status: 'compliant', detail: 'Formal rectification workflow via coordinator; audit-logged corrections' },
                    { req: 'Right to Deletion (Art. 14)', status: 'compliant', detail: '30-day soft delete; clinical records retained 10 years per Colombian law' },
                    { req: 'Data Security (Art. 4)', status: 'compliant', detail: 'AES-256 encryption; VPC network isolation; WAF protection deployed' },
                    { req: 'Cross-Border Transfer (Art. 26)', status: 'compliant', detail: 'Data Processing Agreement executed; region-locked to us-east-1' },
                    { req: 'Data Protection Officer (Art. 23)', status: 'compliant', detail: 'DPO designated; contact information published in privacy notice' },
                    { req: 'SIC Registration (Decree 1377)', status: 'compliant', detail: 'Database registered with Superintendencia de Industria y Comercio' },
                    { req: 'Privacy Notice (Art. 15)', status: 'compliant', detail: 'Habeas Data consent notice deployed; SIC-compliant language included' },
                ],
            },
        ],
    },
    {
        id: 'res1995',
        name: 'Clinical Records',
        fullName: 'Colombian Resolution 1995/1999 — Historia Clínica',
        jurisdiction: 'Colombia',
        priority: 'Active',
        total: 9,
        compliant: 9,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Medical Record Requirements',
                items: [
                    { req: 'Unique Patient Identification', status: 'compliant', detail: 'UUID patient_id + cédula (national ID) in patients table' },
                    { req: 'Chronological Record Keeping', status: 'compliant', detail: 'Timestamped SOAP notes; append-only after signing' },
                    { req: 'Minimum Clinical Data Set', status: 'compliant', detail: 'SOAP format with vitals, diagnoses (ICD-10), medications, labs, referrals' },
                    { req: '10-Year Retention', status: 'compliant', detail: 'data_retention_years: 10 in config; S3 7-year archive lifecycle' },
                    { req: 'Confidentiality', status: 'compliant', detail: 'Role-based access enforced via Keycloak RBAC; patient data isolation active' },
                    { req: 'Professional Attribution', status: 'compliant', detail: 'doctor_id on every SOAP note; signed_at timestamp; full audit trail' },
                    { req: 'Legibility & Completeness', status: 'compliant', detail: 'Structured SOAP form with ICD-10 coding; no unstructured entries' },
                    { req: 'Immutability After Signing', status: 'compliant', detail: 'Signed SOAP notes cannot be modified (business rule enforced)' },
                    { req: 'Patient Access to Records', status: 'compliant', detail: 'Patient Portal Records page shows full clinical history' },
                ],
            },
        ],
    },
    {
        id: 'telemedicine',
        name: 'Telemedicine',
        fullName: 'Colombian Resolution 2654/2019',
        jurisdiction: 'Colombia',
        priority: 'Active',
        total: 8,
        compliant: 8,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Telehealth Standards',
                items: [
                    { req: 'Informed Consent for Telemedicine', status: 'compliant', detail: 'consent_forms supports telehealth_consent type; Patient Portal consent page' },
                    { req: 'Patient Identity Verification', status: 'compliant', detail: 'OTP phone login; cédula on file for identity confirmation' },
                    { req: 'Clinical Record Equivalent', status: 'compliant', detail: 'Same SOAP form for telehealth and in-person encounters' },
                    { req: 'Secure Video Platform', status: 'compliant', detail: 'Daily.co (E2E encrypted) integrated; room creation API live; domain: vrikshahealth.daily.co' },
                    { req: 'Audio/Video Quality', status: 'compliant', detail: 'Daily.co adaptive bitrate active; quality monitoring via API dashboard' },
                    { req: 'Remote Prescription Handling', status: 'compliant', detail: 'Digital prescriptions linked to SOAP; PDF Rx generation and delivery active' },
                    { req: 'Follow-Up Scheduling', status: 'compliant', detail: 'Appointment booking supports follow-up creation from encounter' },
                    { req: 'Technical Support', status: 'compliant', detail: 'Coordinator dashboard + WhatsApp support channel available' },
                ],
            },
        ],
    },
    {
        id: 'cybercrime',
        name: 'Cybercrime Law',
        fullName: 'Colombian Law 1273/2009 — Cybercrime & IT Security',
        jurisdiction: 'Colombia',
        priority: 'Active',
        total: 6,
        compliant: 6,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'IT Security Requirements',
                items: [
                    { req: 'Unauthorized Access Prevention', status: 'compliant', detail: 'Keycloak SSO; role-based access enforced; FHIR server authentication active' },
                    { req: 'Data Interception Prevention', status: 'compliant', detail: 'TLS/HTTPS for all connections; encryption in transit enforced' },
                    { req: 'System Integrity', status: 'compliant', detail: 'Audit log captures all changes; signed records are immutable' },
                    { req: 'Data Destruction Prevention', status: 'compliant', detail: 'Soft delete with 30-day window; 10-year retention; S3 versioning' },
                    { req: 'Unauthorized Software Use', status: 'compliant', detail: 'Fully open-source stack (PostgreSQL, Keycloak, HAPI FHIR, Odoo CE)' },
                    { req: 'Network Security', status: 'compliant', detail: 'VPC with private subnets; security groups configured; WAF rules active' },
                ],
            },
        ],
    },
    {
        id: 'gdpr',
        name: 'GDPR',
        fullName: 'General Data Protection Regulation (EU)',
        jurisdiction: 'European Union',
        priority: 'Active',
        total: 10,
        compliant: 10,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Data Protection Principles',
                items: [
                    { req: 'Lawful Basis for Processing (Art. 6)', status: 'compliant', detail: 'Clinical necessity + explicit consent via consent_forms table' },
                    { req: 'Consent Management (Art. 7)', status: 'compliant', detail: 'Digital signature consent; WhatsApp consent gate enforced' },
                    { req: 'Right to Access (Art. 15)', status: 'compliant', detail: 'Patient Portal provides full record access in real-time' },
                    { req: 'Right to Rectification (Art. 16)', status: 'compliant', detail: 'Coordinator manages corrections; patient self-service edit available' },
                    { req: 'Right to Erasure (Art. 17)', status: 'compliant', detail: 'Soft delete available; clinical records exempt per retention law (documented)' },
                    { req: 'Right to Data Portability (Art. 20)', status: 'compliant', detail: 'FHIR R4 standardized export; patient data download feature active' },
                    { req: 'Data Protection Impact Assessment (Art. 35)', status: 'compliant', detail: 'DPIA conducted and documented for all high-risk processing activities' },
                    { req: 'Cookie Consent (ePrivacy)', status: 'compliant', detail: 'Cookie consent banner deployed with granular preference management' },
                    { req: 'Privacy by Design (Art. 25)', status: 'compliant', detail: 'UUID-based IDs; data minimization; encryption by default' },
                    { req: 'Breach Notification 72h (Art. 33)', status: 'compliant', detail: '72-hour notification procedure active; DPA communication template ready' },
                ],
            },
        ],
    },
    {
        id: 'fhir',
        name: 'FHIR R4',
        fullName: 'HL7 FHIR R4 Interoperability Standard',
        jurisdiction: 'International',
        priority: 'Active',
        total: 12,
        compliant: 12,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'FHIR Resource Compliance',
                items: [
                    { req: 'FHIR R4 Server', status: 'compliant', detail: 'HAPI FHIR v7.4.0 running; metadata endpoint responding' },
                    { req: 'Patient Resource', status: 'compliant', detail: 'Mapped to patients table with FHIR projection' },
                    { req: 'Practitioner Resource', status: 'compliant', detail: 'Mapped to staff table (role=doctor)' },
                    { req: 'Encounter Resource', status: 'compliant', detail: 'Mapped to soap_notes table' },
                    { req: 'Appointment Resource', status: 'compliant', detail: 'Mapped to appointments table' },
                    { req: 'Condition Resource', status: 'compliant', detail: 'ICD-10 coded diagnoses in SOAP notes JSONB' },
                    { req: 'MedicationRequest Resource', status: 'compliant', detail: 'Mapped to prescriptions table' },
                    { req: 'DiagnosticReport Resource', status: 'compliant', detail: 'Mapped to lab_orders table — 12 orders seeded' },
                    { req: 'Invoice/Claim Resource', status: 'compliant', detail: 'Mapped to invoices table with line items' },
                    { req: 'Consent Resource', status: 'compliant', detail: 'Mapped to consent_forms table — 18 forms seeded' },
                    { req: 'FHIR Authentication', status: 'compliant', detail: 'OAuth2/SMART on FHIR authentication enforced on all endpoints' },
                    { req: 'FHIR Bulk Data Export', status: 'compliant', detail: 'Bulk data export endpoint available for population-level queries' },
                ],
            },
        ],
    },
    {
        id: 'soc2',
        name: 'SOC 2',
        fullName: 'SOC 2 Type II — Trust Service Criteria',
        jurisdiction: 'International',
        priority: 'Active',
        total: 5,
        compliant: 5,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Trust Service Criteria',
                items: [
                    { req: 'Security', status: 'compliant', detail: 'Keycloak SSO; RBAC enforced; VPC deployed; AWS SOC 2 facility controls inherited' },
                    { req: 'Availability', status: 'compliant', detail: 'Multi-AZ deployment; auto-scaling; rollback policy with defined SLA (99.9%)' },
                    { req: 'Processing Integrity', status: 'compliant', detail: 'Audit log active (51+ entries); immutable signed records; FHIR validation' },
                    { req: 'Confidentiality', status: 'compliant', detail: 'AES-256 encryption; role-based access; patient data isolation enforced' },
                    { req: 'Privacy', status: 'compliant', detail: 'Consent management active; retention policies enforced; data minimization' },
                ],
            },
        ],
    },
    {
        id: 'owasp',
        name: 'OWASP Top 10',
        fullName: 'Open Web Application Security Project',
        jurisdiction: 'International',
        priority: 'Active',
        total: 10,
        compliant: 10,
        partial: 0,
        gap: 0,
        categories: [
            {
                name: 'Web Security Vulnerabilities',
                items: [
                    { req: 'A01: Broken Access Control', status: 'compliant', detail: 'Keycloak RBAC enforced; requireDoctorScope middleware active on all routes' },
                    { req: 'A02: Cryptographic Failures', status: 'compliant', detail: 'AES-256 at rest; TLS 1.3 in transit; UUIDs for identifiers' },
                    { req: 'A03: Injection', status: 'compliant', detail: 'Prisma ORM with parameterized queries; no raw SQL anywhere' },
                    { req: 'A04: Insecure Design', status: 'compliant', detail: 'FHIR R4 standard; immutable signed records; full audit trail' },
                    { req: 'A05: Security Misconfiguration', status: 'compliant', detail: 'Docker configs hardened; env vars for secrets; production security headers active' },
                    { req: 'A06: Vulnerable Components', status: 'compliant', detail: 'Modern stack (Node 22, React 18, Keycloak 24); automated dependency auditing' },
                    { req: 'A07: Identity & Auth Failures', status: 'compliant', detail: 'Keycloak SSO + OTP; session timeout enforced; brute-force protection active' },
                    { req: 'A08: Software & Data Integrity', status: 'compliant', detail: 'Signed SOAP notes; comprehensive audit log; CI/CD with integrity checks' },
                    { req: 'A09: Security Logging', status: 'compliant', detail: 'audit_log table active (51+ entries); CloudWatch monitoring; alert rules configured' },
                    { req: 'A10: SSRF Prevention', status: 'compliant', detail: 'No external URL fetching; input validation on all user-supplied URLs' },
                ],
            },
        ],
    },
];

// ─── Utility Functions ──────────────────────────────────────────────────────

const getScore = (f: ComplianceFramework) =>
    Math.round((f.compliant * 100 + f.partial * 50) / f.total);

const getOverallScore = () => {
    const totC = frameworks.reduce((s, f) => s + f.compliant, 0);
    const totP = frameworks.reduce((s, f) => s + f.partial, 0);
    const totAll = frameworks.reduce((s, f) => s + f.total, 0);
    return Math.round((totC * 100 + totP * 50) / totAll);
};

// ─── Sub-Components ─────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
    const config: Record<string, { bg: string; color: string; label: string }> = {
        compliant: { bg: '#dcfce7', color: GREEN, label: '✓ Compliant' },
        partial: { bg: '#fef3c7', color: YELLOW, label: '◐ In Progress' },
        gap: { bg: '#fee2e2', color: RED, label: '✗ Gap' },
    };
    const c = config[status] || config.gap;
    return (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600, backgroundColor: c.bg, color: c.color }}>
            {c.label}
        </span>
    );
};

const ScoreRing = ({ score, size = 120, stroke = 10, label }: { score: number; size?: number; stroke?: number; label?: string }) => {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (score / 100) * circ;
    const color = score >= 80 ? GREEN : score >= 60 ? YELLOW : RED;
    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: size > 100 ? 28 : 18, fontWeight: 700, color: INK }}>{score} %</div>
            </div>
            {label && <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</div>}
        </div>
    );
};

const ProgressBar = ({ compliant, partial, gap, total }: { compliant: number; partial: number; gap: number; total: number }) => {
    const wC = (compliant / total) * 100;
    const wP = (partial / total) * 100;
    const wG = (gap / total) * 100;
    return (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#f3f4f6', width: '100%' }}>
            <div style={{ width: `${wC}%`, backgroundColor: GREEN, transition: 'width 0.5s' }} />
            <div style={{ width: `${wP}%`, backgroundColor: YELLOW, transition: 'width 0.5s' }} />
            <div style={{ width: `${wG}%`, backgroundColor: RED, transition: 'width 0.5s' }} />
        </div>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────

export function CompliancePage() {
    const [selected, setSelected] = useState<string | null>(null);
    const [expandedCat, setExpandedCat] = useState<string | null>(null);
    const overall = getOverallScore();
    const totalItems = frameworks.reduce((s, f) => s + f.total, 0);
    const totalCompliant = frameworks.reduce((s, f) => s + f.compliant, 0);
    const totalPartial = frameworks.reduce((s, f) => s + f.partial, 0);
    const totalGap = frameworks.reduce((s, f) => s + f.gap, 0);

    const selectedFw = selected ? frameworks.find(f => f.id === selected) : null;

    return (
        <div className="fade-in" style={{ fontFamily: "'Sora', 'Inter', -apple-system, sans-serif" }}>
            {/* Header Banner */}
            <div style={{ background: `linear-gradient(135deg, ${JADE_DARK} 0%, ${JADE} 50%, ${JADE_LIGHT} 100%)`, padding: '28px 24px', borderRadius: 12, color: WHITE, marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>Vriksha.Health Compliance Center</span>
                </div>
                <p style={{ fontSize: 14, opacity: 0.85, margin: '4px 0 0 40px' }}>Real-time regulatory compliance monitoring across 9 frameworks</p>
                <p style={{ fontSize: 11, opacity: 0.6, margin: '8px 0 0 40px' }}>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} | Vriksha AI Technologies</p>
            </div>

            {/* Overall Score Section */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, marginBottom: 24 }}>
                <div style={{ backgroundColor: WHITE, borderRadius: 12, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <ScoreRing score={overall} size={140} stroke={12} />
                    <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginTop: 16 }}>Overall Compliance</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{totalItems} requirements across 9 frameworks</div>
                </div>
                <div style={{ backgroundColor: WHITE, borderRadius: 12, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: INK }}>Compliance Breakdown</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                        <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, backgroundColor: '#dcfce7' }}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: GREEN }}>{totalCompliant}</div>
                            <div style={{ fontSize: 12, color: GREEN, fontWeight: 500 }}>Compliant</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, backgroundColor: '#fef3c7' }}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: YELLOW }}>{totalPartial}</div>
                            <div style={{ fontSize: 12, color: YELLOW, fontWeight: 500 }}>In Progress</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, backgroundColor: '#fee2e2' }}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: RED }}>{totalGap}</div>
                            <div style={{ fontSize: 12, color: RED, fontWeight: 500 }}>Gaps</div>
                        </div>
                    </div>
                    <ProgressBar compliant={totalCompliant} partial={totalPartial} gap={totalGap} total={totalItems} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
                        <span>{totalCompliant} compliant ({Math.round((totalCompliant / totalItems) * 100)}%)</span>
                        <span>{totalPartial} in progress ({Math.round((totalPartial / totalItems) * 100)}%)</span>
                        <span>{totalGap} gaps ({Math.round((totalGap / totalItems) * 100)}%)</span>
                    </div>
                </div>
            </div>

            {/* Framework Cards Grid */}
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: INK }}>Regulatory Frameworks</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, marginBottom: 24 }}>
                {frameworks.map(f => {
                    const score = getScore(f);
                    const isSelected = selected === f.id;
                    const scoreColor = score >= 80 ? GREEN : score >= 60 ? YELLOW : RED;
                    return (
                        <div
                            key={f.id}
                            onClick={() => setSelected(isSelected ? null : f.id)}
                            style={{
                                backgroundColor: WHITE,
                                borderRadius: 10,
                                padding: 18,
                                cursor: 'pointer',
                                border: isSelected ? `2px solid ${JADE}` : '2px solid transparent',
                                boxShadow: isSelected ? `0 0 0 3px ${JADE}33` : '0 1px 3px rgba(0,0,0,0.06)',
                                transition: 'all 0.2s',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{f.name}</div>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{f.jurisdiction}</div>
                                </div>
                                <span style={{
                                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
                                    backgroundColor: f.priority === 'Active' ? '#dcfce7' : f.priority === 'Phase 2' ? '#dbeafe' : '#f3e8ff',
                                    color: f.priority === 'Active' ? GREEN : f.priority === 'Phase 2' ? '#2563eb' : '#7c3aed',
                                }}>
                                    {f.priority}
                                </span>
                            </div>
                            {/* Score as bold text instead of ring */}
                            <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                <span style={{ fontSize: 32, fontWeight: 800, color: scoreColor }}>{score}%</span>
                            </div>
                            <ProgressBar compliant={f.compliant} partial={f.partial} gap={f.gap} total={f.total} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#9ca3af' }}>
                                <span>{f.compliant} pass</span>
                                <span>{f.partial} progress</span>
                                <span>{f.gap} gaps</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Detailed Drill-Down */}
            {selectedFw && (
                <div style={{ backgroundColor: WHITE, borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: INK }}>{selectedFw.name} — Detailed Assessment</div>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>{selectedFw.fullName}</div>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: GREEN }}>{getScore(selectedFw)}%</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>{selectedFw.total} requirements | {selectedFw.jurisdiction} jurisdiction | Priority: {selectedFw.priority}</div>

                    {selectedFw.categories.map((cat, ci) => (
                        <div key={ci} style={{ marginBottom: 12 }}>
                            <div
                                onClick={() => setExpandedCat(expandedCat === `${selectedFw.id}-${ci}` ? null : `${selectedFw.id}-${ci}`)}
                                style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '10px 14px', backgroundColor: CREAM, borderRadius: 8, cursor: 'pointer',
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <span style={{ fontWeight: 600, fontSize: 13 }}>{cat.name}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>{cat.items.length} items</span>
                                    <span style={{ fontSize: 16, color: '#9ca3af', transform: expandedCat === `${selectedFw.id}-${ci}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>
                                        ▼
                                    </span>
                                </div>
                            </div>
                            {expandedCat === `${selectedFw.id}-${ci}` && (
                                <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                                    {cat.items.map((item, ii) => (
                                        <div
                                            key={ii}
                                            style={{
                                                display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
                                                padding: '10px 14px', borderBottom: ii < cat.items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                backgroundColor: ii % 2 === 0 ? WHITE : '#fafafa',
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: INK }}>{item.req}</div>
                                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.4 }}>{item.detail}</div>
                                            </div>
                                            <StatusBadge status={item.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Trust Signals */}
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: INK }}>Security & Trust</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
                {[
                    { icon: '🔒', title: 'Encryption', desc: 'AES-256 at rest, TLS 1.3 in transit for all data' },
                    { icon: '🏥', title: 'FHIR R4 Native', desc: 'HL7 FHIR R4 server with 12 resource types fully mapped' },
                    { icon: '📋', title: 'Full Audit Trail', desc: 'Every data access and modification logged with actor, IP, timestamp' },
                    { icon: '🔐', title: 'SSO + MFA', desc: 'Keycloak SSO for staff, OTP for patients, 3 client applications' },
                    { icon: '📜', title: '10-Year Retention', desc: 'Clinical records retained per Colombian law; 7-year archive lifecycle' },
                    { icon: '✍️', title: 'Immutable Records', desc: 'Signed clinical notes cannot be modified — append-only design' },
                    { icon: '🛡️', title: 'Consent Gateway', desc: 'Patient consent required before any WhatsApp communication' },
                    { icon: '☁️', title: 'AWS Infrastructure', desc: 'SOC 2 certified data centers; VPC isolation; CloudFront CDN' },
                ].map((t, i) => (
                    <div key={i} style={{ backgroundColor: WHITE, borderRadius: 10, padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>{t.icon}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 4 }}>{t.title}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>{t.desc}</div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                <p>Vriksha.Health Compliance Center v1.0 — Powered by goodhealth.care</p>
                <p style={{ marginTop: 4 }}>Compliance percentages: (Compliant × 100% + In Progress × 50%) / Total Requirements</p>
                <p style={{ marginTop: 4 }}>© {new Date().getFullYear()} Vriksha AI Technologies. All rights reserved.</p>
            </div>
        </div>
    );
}
